import { useState, useEffect } from 'react';
import { offerStoreAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Loader2, Pencil, Trash2 } from 'lucide-react';

export function OfferDetailModal({ offerId, onClose, onChanged }) {
  const { canAccess } = useAuth();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const canEdit = canAccess('OFFER_STORE', 'EDIT');
  const canDelete = canAccess('OFFER_STORE', 'DELETE');

  useEffect(() => {
    offerStoreAPI.get(offerId).then(r => { setOffer(r.data); setForm(r.data); }).catch(e => toast.error(e.response?.data?.detail || 'Failed to load offer')).finally(() => setLoading(false));
  }, [offerId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await offerStoreAPI.update(offerId, {
        model_raw: form.model_raw, description: form.description,
        price: form.price === '' ? null : Number(form.price), qty: form.qty === '' ? null : Number(form.qty),
        availability: form.availability, lead_time_days: form.lead_time_days === '' ? null : Number(form.lead_time_days),
        availability_note: form.availability_note, offer_date: form.offer_date,
      });
      setOffer(r.data); setForm(r.data); setEditing(false);
      toast.success('Offer updated');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update offer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteReason.trim()) { toast.error('A reason is required'); return; }
    setDeleting(true);
    try {
      await offerStoreAPI.remove(offerId, deleteReason);
      toast.success('Offer deleted');
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete offer');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!offerId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="offer-detail-modal">
        <DialogHeader><DialogTitle>Offer detail</DialogTitle></DialogHeader>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" />
        ) : !offer ? null : confirmDelete ? (
          <div className="space-y-3">
            <p className="text-sm">Deleting this offer requires a reason. It will be hidden from search and export.</p>
            <div><Label>Reason *</Label><Textarea value={deleteReason} onChange={e => setDeleteReason(e.target.value)} className="rounded-sm mt-1" data-testid="offer-delete-reason-input" /></div>
          </div>
        ) : editing ? (
          <div className="space-y-3">
            <div><Label>Model</Label><Input value={form.model_raw} onChange={e => set('model_raw', e.target.value)} className="rounded-sm mt-1" data-testid="offer-edit-model-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price (USD)</Label><Input type="number" value={form.price ?? ''} onChange={e => set('price', e.target.value)} className="rounded-sm mt-1" data-testid="offer-edit-price-input" /></div>
              <div><Label>Qty</Label><Input type="number" value={form.qty ?? ''} onChange={e => set('qty', e.target.value)} className="rounded-sm mt-1" data-testid="offer-edit-qty-input" /></div>
            </div>
            <div><Label>Offer date</Label><Input type="date" value={form.offer_date || ''} onChange={e => set('offer_date', e.target.value)} className="rounded-sm mt-1" data-testid="offer-edit-date-input" /></div>
            <div><Label>Description</Label><Textarea value={form.description || ''} onChange={e => set('description', e.target.value)} className="rounded-sm mt-1" data-testid="offer-edit-description-input" /></div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="flex items-start justify-between">
              <p className="font-medium text-base">{offer.model_raw}</p>
              {offer.matched_sku ? <Badge variant="outline" className="rounded-sm text-xs">{offer.matched_sku}</Badge> : <Badge variant="outline" className="rounded-sm text-xs bg-slate-50 text-slate-600">Not in catalogue</Badge>}
            </div>
            {offer.description && <p className="text-muted-foreground">{offer.description}</p>}
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Price:</span> {offer.price != null ? `$${Number(offer.price).toFixed(2)}` : '—'}</div>
              <div><span className="text-muted-foreground">Qty:</span> {offer.qty ?? '—'}</div>
              <div><span className="text-muted-foreground">Vendor:</span> {offer.vendor_name_raw}</div>
              <div><span className="text-muted-foreground">Country:</span> {offer.vendor_country || '—'}</div>
              <div><span className="text-muted-foreground">Availability:</span> {offer.availability === 'LEAD_TIME' ? `Lead time ${offer.lead_time_days ?? ''}d` : 'Ready stock'}</div>
              <div><span className="text-muted-foreground">Offer date:</span> {offer.offer_date}</div>
              <div><span className="text-muted-foreground">Source:</span> {offer.source}</div>
              <div><span className="text-muted-foreground">Entered by:</span> {offer.created_by_name}</div>
            </div>
            {offer.availability_note && <p className="text-xs text-muted-foreground">Vendor's text: "{offer.availability_note}"</p>}
          </div>
        )}
        <DialogFooter>
          {confirmDelete ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => setConfirmDelete(false)} data-testid="offer-delete-cancel-btn">Back</Button>
              <Button variant="destructive" className="rounded-sm" onClick={handleDelete} disabled={deleting} data-testid="offer-delete-confirm-btn">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Delete offer
              </Button>
            </>
          ) : editing ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => setEditing(false)} data-testid="offer-edit-cancel-btn">Cancel</Button>
              <Button className="rounded-sm" onClick={handleSave} disabled={saving} data-testid="offer-edit-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save
              </Button>
            </>
          ) : (
            <>
              {canDelete && <Button variant="outline" className="rounded-sm text-destructive" onClick={() => setConfirmDelete(true)} data-testid="offer-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Delete</Button>}
              {canEdit && <Button variant="outline" className="rounded-sm" onClick={() => setEditing(true)} data-testid="offer-edit-btn"><Pencil className="h-4 w-4 mr-2" />Edit</Button>}
              <Button className="rounded-sm" onClick={onClose} data-testid="offer-detail-close-btn">Close</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
